import { Injectable } from '@angular/core'
import { FirestoreQueryBuilder, OrderByDirection, SubcollectionRepository } from '@engineering11/web-api-firebase'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { COLLECTIONS } from 'shared-lib'
import { ClientToolName, IClientToolCall } from '../models/assistant-client-tool.model'
import { IAssistantRunStep, RunStepStatus } from '../models/assistant.model'

function stepsPath(conversationId: string, id = ''): string {
  return `${COLLECTIONS.ASSISTANT_CONVERSATION}${conversationId}${COLLECTIONS.ASSISTANT_CONVERSATION_STEPS}${id}`
}

// The backend writes a client-tool step as flat fields (kind/toolName/argsJson), not the `clientTool`
// object the executor reads off the step. Fields the model type does not declare, read here.
interface BackendStepFields {
  kind?: string
  toolName?: string
  argsJson?: string
}

/**
 * Synthesize the `clientTool` marker the executor detects, normalizing each tool's args to what the
 * client handlers expect — the backend and frontend name these args differently (navigate: `route` here
 * vs `target` in the handler; highlight: `target` here vs `anchor` in the handler). Only a still-pending
 * client-tool step gets a marker, so a resolved step or a model-turn step is never (re-)dispatched.
 */
function clientToolCallOf(step: IAssistantRunStep): IClientToolCall | undefined {
  const raw = step as IAssistantRunStep & BackendStepFields
  if (raw.kind !== 'client-tool' || step.status !== RunStepStatus.AwaitingClient || !raw.toolName) {
    return undefined
  }
  let args: Record<string, unknown> = {}
  try {
    args = raw.argsJson ? JSON.parse(raw.argsJson) : {}
  } catch {
    args = {}
  }
  switch (raw.toolName) {
    case ClientToolName.Navigate:
      return { name: ClientToolName.Navigate, args: { target: args.route as string } }
    case ClientToolName.Highlight:
      return { name: ClientToolName.Highlight, args: { anchor: args.target as string } }
    case ClientToolName.StartTour:
      return { name: ClientToolName.StartTour, args: {} }
    default:
      return undefined
  }
}

/**
 * Live-listener store for the per-conversation run-step subcollection
 * (`assistant_conversations/{id}/steps`) the backend writes run steps — including `client_tool_call`
 * steps — to (CAP-2). Filtered by the caller's Firebase auth uid + customerKey so the owner firestore
 * rule allows the read; ordered by `__createdAt` ascending. Mirrors `job-application-comment.repository.ts`.
 */
@Injectable({ providedIn: 'root' })
export class AssistantConversationStepsRepository extends SubcollectionRepository<IAssistantRunStep, typeof stepsPath> {
  protected buildPath = stepsPath

  /** Live, ordered run steps for a conversation owned by (authUid, customerKey). Emits every Firestore update. */
  liveSteps(conversationId: string, authUid: string, customerKey: string): Observable<IAssistantRunStep[]> {
    const query = new FirestoreQueryBuilder<IAssistantRunStep>()
      .where('authUid', '==', authUid)
      .where('customerKey', '==', customerKey)
      .orderBy('__createdAt', OrderByDirection.Asc)
      .build()
    return super.queryValueChanges(query, conversationId).pipe(map(steps => steps.map(step => ({ ...step, clientTool: clientToolCallOf(step) }))))
  }
}
